import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@mui/material';
import _ from 'lodash';
import { FunctionComponent, PropsWithChildren, SubmitEvent, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { gameConfigStore, useGameConfigSelector } from '../../store/GameConfigStore';
import { gamePlayStore, useGameplaySelector } from '../../store/GameplayStore';
import { Team } from '../../types/GameplayTypes';
import { useDialogAnimation } from '../../util/Animation';
import { consumeKeyEvents } from '../../util/Keyboard';
import { muiStyleSheet } from '../../util/Styles';

type Props = {
	team: Team;
	open: boolean;
	onClose: () => void;
};

export const AdjustScoreDialog: FunctionComponent<Props> = (props) => {
	const { open, team } = props;
	const teamName = useGameConfigSelector(gameConfigStore.selectors.teamNames)[team];
	const currentScore = useGameplaySelector(gamePlayStore.selectors.teamScores)[team];
	const dispatch = useDispatch();
	const { dialogProps } = useDialogAnimation();

	const [score, setScore] = useState<number | ''>(currentScore);

	const MAX_SCORE = 999;

	useEffect(function resetScoreOnOpen() {
		if (open) {
			setScore(currentScore);
		}
	}, [open, currentScore]);

	function save(e: SubmitEvent) {
		e.preventDefault();
		props.onClose();
		if (score !== '') {
			dispatch(gamePlayStore.actions.adjustScore({ team, score }));
		}
	}

	return <Dialog open={open} onClose={props.onClose} {...dialogProps} {...consumeKeyEvents}>
		<DialogTitle sx={{ paddingBottom: 0 }}>Adjust {teamName}&apos;s Score</DialogTitle>
		<DialogContent>
			<form id='score-adjust-form' onSubmit={save}>
				<TextField type="Number" label="Score"
					value={score}
					onChange={(e) => setScore(e.target.value === '' ? '' : _.clamp(Number(e.target.value), 0, MAX_SCORE))}
					fullWidth
					slotProps={{ htmlInput: { min: 0, max: MAX_SCORE, autoFocus: true } }}
					sx={{ marginTop: 2 }}
				/>
			</form>
		</DialogContent>
		<DialogActions>
			<Button onClick={props.onClose}>Cancel</Button>
			<Button type='submit' form='score-adjust-form' variant="contained" disabled={score === ''}>Save</Button>
		</DialogActions>
	</Dialog>;
};

type AdjustScoreButtonProps = PropsWithChildren<{
	team: Team;
}>;

/** Button that opens the AdjustScoreDialog */
export const AdjustScoreButton: FunctionComponent<AdjustScoreButtonProps> = (props) => {
	const { team } = props;
	const teamNames = useGameConfigSelector(gameConfigStore.selectors.teamNames);

	const [dialogOpen, setDialogOpen] = useState(false);

	return <>
		<Box component="button" sx={styles.scoreButton}
			onClick={() => setDialogOpen(true)}
			aria-label={`Adjust score for ${teamNames[team]}`}
		>
			{props.children}
		</Box>
		<AdjustScoreDialog open={dialogOpen} team={team} onClose={() => setDialogOpen(false)} />
	</>;
};

const styles = muiStyleSheet({
	scoreButton: {
		border: 'none',
		background: 'none',
		padding: 0,
		cursor: 'pointer',
		overflow: 'visible',
	},
});